// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'lib/common';
import * as ShippingAddressesAPI from 'lib/api/me/shippingAddresses';

/* ACTION TYPE */
const GET_SHIPPING_ADDRESSES = 'shippingAddresses/GET_SHIPPING_ADDRESSES';
const CREATE_SHIPPING_ADDRESS = 'shippingAddresses/CREATE_SHIPPING_ADDRESS';
const UPDATE_SHIPPING_ADDRESS = 'shippingAddresses/UPDATE_SHIPPING_ADDRESS';
const DELETE_SHIPPING_ADDRESS = 'shippingAddresses/DELETE_SHIPPING_ADDRESS';
const SET_DEFAULT_SHIPPING_ADDRESS = 'shippingAddresses/SET_DEFAULT_SHIPPING_ADDRESS';
const SET_SELECTED_ADDRESS = 'shippingAddresses/SET_SELECTED_ADDRESS';
const CLEAR_SELECTED_ADDRESS = 'shippingAddresses/CLEAR_SELECTED_ADDRESS';

/* ACTION CREATOR */
const getShippingAddresses = createAction(
  GET_SHIPPING_ADDRESSES,
  ShippingAddressesAPI.getShippingAddresses,
);
const createShippingAddress = createAction(
  CREATE_SHIPPING_ADDRESS,
  ShippingAddressesAPI.createShippingAddress,
);
const updateShippingAddress = createAction(
  UPDATE_SHIPPING_ADDRESS,
  ShippingAddressesAPI.updateShippingAddress,
);
const deleteShippingAddress = createAction(
  DELETE_SHIPPING_ADDRESS,
  ShippingAddressesAPI.deleteShippingAddress,
);
const setDefaultShippingAddress = createAction(
  SET_DEFAULT_SHIPPING_ADDRESS,
  ShippingAddressesAPI.setDefaultShippingAddress,
);
const setSelectedAddress = createAction(SET_SELECTED_ADDRESS);
const clearSelectedAddress = createAction(CLEAR_SELECTED_ADDRESS);

/* ACTION FLOW TYPE */
type GetShippingAddressesAction = ActionType<typeof getShippingAddresses>;
type CreateShippingAddressAction = ActionType<typeof createShippingAddress>;
type UpdateShippingAddressAction = ActionType<typeof updateShippingAddress>;
type DeleteShippingAddressAction = ActionType<typeof deleteShippingAddress>;
type SetDefaultShippingAddressAction = ActionType<typeof setDefaultShippingAddress>;
type SetSelectedAddressAction = ActionType<typeof setSelectedAddress>;
type ClearSelectedAddressAction = ActionType<typeof clearSelectedAddress>;

/* ACTION CREATORS INTERFACE */
export interface ShippingAddressesActionCreators {
  getShippingAddresses(): any;
  createShippingAddress(addressData: any): any;
  updateShippingAddress(id: string, addressData: any): any;
  deleteShippingAddress(id: string): any;
  setDefaultShippingAddress(id: string): any;
  setSelectedAddress(address: any): any;
  clearSelectedAddress(): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators: ShippingAddressesActionCreators = {
  getShippingAddresses,
  createShippingAddress,
  updateShippingAddress,
  deleteShippingAddress,
  setDefaultShippingAddress,
  setSelectedAddress,
  clearSelectedAddress,
};

/* STATE TYPES */
export type ShippingAddresses = {
  list: Array<any>,
  selectedAddress: any,
  loading: boolean,
  error: string | null,
};

/* INITIAL STATE */
const initialState: ShippingAddresses = {
  list: [],
  selectedAddress: null,
  loading: false,
  error: null,
};

/* REDUCER */
const reducer = handleActions(
  {
    [GET_SHIPPING_ADDRESSES]: (state, action: GetShippingAddressesAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.list = (action.payload && action.payload.shippingAddressList) || [];
        draft.loading = false;
        draft.error = null;
      });
    },
    [CREATE_SHIPPING_ADDRESS]: (state, action: CreateShippingAddressAction) => {
      return produce(state, draft => {
        if (!action) return;
        // 새로 생성된 배송지를 목록에 추가
        if (action.payload && action.payload.shippingAddress) {
          draft.list.unshift(action.payload.shippingAddress);
        }
        draft.loading = false;
        draft.error = null;
      });
    },
    [UPDATE_SHIPPING_ADDRESS]: (state, action: UpdateShippingAddressAction) => {
      return produce(state, draft => {
        if (!action) return;
        // 업데이트된 배송지를 목록에서 찾아서 교체
        const index = draft.list.findIndex(addr => addr.id === (action.meta && action.meta.id));
        if (index !== -1 && action.payload && action.payload.shippingAddress) {
          draft.list[index] = action.payload.shippingAddress;
        }
        draft.loading = false;
        draft.error = null;
      });
    },
    [DELETE_SHIPPING_ADDRESS]: (state, action: DeleteShippingAddressAction) => {
      return produce(state, draft => {
        if (!action) return;
        // 삭제된 배송지를 목록에서 제거
        draft.list = draft.list.filter(addr => addr.id !== (action.meta && action.meta.id));
        // 선택된 배송지가 삭제된 경우 초기화
        if (draft.selectedAddress && draft.selectedAddress.id === (action.meta && action.meta.id)) {
          draft.selectedAddress = null;
        }
        draft.loading = false;
        draft.error = null;
      });
    },
    [SET_DEFAULT_SHIPPING_ADDRESS]: (state, action: SetDefaultShippingAddressAction) => {
      return produce(state, draft => {
        if (!action) return;
        // 모든 배송지의 기본 설정을 해제하고, 선택된 배송지만 기본으로 설정
        draft.list.forEach(addr => {
          addr.is_default = addr.id === (action.meta && action.meta.id);
        });
        draft.loading = false;
        draft.error = null;
      });
    },
    [SET_SELECTED_ADDRESS]: (state, action: SetSelectedAddressAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.selectedAddress = action.payload;
      });
    },
    [CLEAR_SELECTED_ADDRESS]: (state, action: ClearSelectedAddressAction) => {
      return produce(state, draft => {
        draft.selectedAddress = null;
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_SHIPPING_ADDRESSES,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error =
          (action.payload && action.payload.message) || '배송지 목록을 불러오는데 실패했습니다.';
      }),
  },
  {
    type: CREATE_SHIPPING_ADDRESS,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error = (action.payload && action.payload.message) || '배송지 추가에 실패했습니다.';
      }),
  },
  {
    type: UPDATE_SHIPPING_ADDRESS,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error = (action.payload && action.payload.message) || '배송지 수정에 실패했습니다.';
      }),
  },
  {
    type: DELETE_SHIPPING_ADDRESS,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error = (action.payload && action.payload.message) || '배송지 삭제에 실패했습니다.';
      }),
  },
  {
    type: SET_DEFAULT_SHIPPING_ADDRESS,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error =
          (action.payload && action.payload.message) || '기본 배송지 설정에 실패했습니다.';
      }),
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/shippingAddresses.js